import React, { useContext, useEffect, useMemo, useState } from "react";
import { Spin } from "antd";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title as ChartTitle,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
  Filler,
} from "chart.js";
import LandingLayout from "./LandingLayoutRedesign";
import { ThemeContext } from "../../context/ThemeContext";
import apiClient from "../../services/apiClient";
import "./MarketInsightsRedesign.css";
import "./CallMoneyChartPage.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ChartTitle,
  ChartTooltip,
  ChartLegend,
  Filler
);

const RANGE_OPTIONS = [
  { key: "30", label: "30D", days: 30 },
  { key: "90", label: "90D", days: 90 },
  { key: "180", label: "180D", days: 180 },
  { key: "all", label: "All", days: null },
];

const hexToRgba = (hex, alpha) => {
  const raw = hex.replace("#", "");
  const full = raw.length === 3 ? raw.split("").map((c) => c + c).join("") : raw;
  const n = parseInt(full, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const formatDateLabel = (iso, prevIso) => {
  if (!iso) return "";
  try {
    const d = new Date(`${iso}T00:00:00`);
    const month = d.toLocaleDateString("en-GB", { month: "short" });
    const day = d.getDate();
    const year = String(d.getFullYear()).slice(-2);
    if (!prevIso) return `${month} '${year}`;
    const prev = new Date(`${prevIso}T00:00:00`);
    if (prev.getMonth() !== d.getMonth() || prev.getFullYear() !== d.getFullYear()) {
      return `${month} '${year}`;
    }
    return String(day);
  } catch {
    return iso;
  }
};

const CallMoneyChartPage = () => {
  const { isDark } = useContext(ThemeContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [points, setPoints] = useState([]);
  const [rangeKey, setRangeKey] = useState("90");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const baseUrl =
          process.env.REACT_APP_BASE_URL || "http://localhost:8083/api/v1/sb";
        const response = await apiClient.get(`${baseUrl}/rbi/money-market/call-money`);
        const result = response.data;
        const payload =
          result?.statusCode === 200 || result?.statusCode === undefined
            ? result?.data ?? result
            : null;
        if (!Array.isArray(payload)) {
          throw new Error(result?.message || "Unexpected API response");
        }
        if (!cancelled) {
          setPoints(
            payload
              .filter((p) => p?.reportDate && p?.weighted != null)
              .map((p) => ({
                reportDate: String(p.reportDate),
                weighted: Number(p.weighted),
              }))
              .filter((p) => !Number.isNaN(p.weighted))
          );
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.message || err.message || "Failed to load Call Money data");
          setPoints([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredPoints = useMemo(() => {
    const option = RANGE_OPTIONS.find((o) => o.key === rangeKey) || RANGE_OPTIONS[1];
    if (!option.days || points.length === 0) return points;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - option.days);
    const cutoffIso = cutoff.toISOString().slice(0, 10);
    return points.filter((p) => p.reportDate >= cutoffIso);
  }, [points, rangeKey]);

  const latest = filteredPoints.length
    ? filteredPoints[filteredPoints.length - 1]
    : null;

  const chartData = useMemo(() => {
    const labels = filteredPoints.map((p, i) =>
      formatDateLabel(p.reportDate, i > 0 ? filteredPoints[i - 1].reportDate : null)
    );
    const values = filteredPoints.map((p) => p.weighted);
    const lineColor = isDark ? "#9fe870" : "#163300";
    return {
      labels,
      datasets: [
        {
          label: "Weighted avg rate (%)",
          data: values,
          borderColor: lineColor,
          backgroundColor: (context) => {
            const { chart } = context;
            const { ctx, chartArea } = chart;
            if (!chartArea) return hexToRgba(lineColor, 0.12);
            const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
            gradient.addColorStop(0, hexToRgba(lineColor, 0.35));
            gradient.addColorStop(1, hexToRgba(lineColor, 0.02));
            return gradient;
          },
          fill: true,
          tension: 0.25,
          pointRadius: filteredPoints.length > 120 ? 0 : 2,
          pointHoverRadius: 4,
          borderWidth: 2,
        },
      ],
    };
  }, [filteredPoints, isDark]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items) => {
              const idx = items?.[0]?.dataIndex;
              return filteredPoints[idx]?.reportDate || "";
            },
            label: (item) => `Weighted: ${Number(item.raw).toFixed(2)}%`,
          },
        },
      },
      scales: {
        x: {
          ticks: {
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 10,
            color: isDark ? "#b8b9b4" : "#868685",
          },
          grid: { color: isDark ? "rgba(244,244,240,0.06)" : "rgba(14,15,12,0.06)" },
        },
        y: {
          title: {
            display: true,
            text: "Rate (%)",
            color: isDark ? "#b8b9b4" : "#454745",
          },
          ticks: { color: isDark ? "#b8b9b4" : "#868685" },
          grid: { color: isDark ? "rgba(244,244,240,0.06)" : "rgba(14,15,12,0.06)" },
        },
      },
    }),
    [filteredPoints, isDark]
  );

  return (
    <LandingLayout>
      <div className={`mi-page cm-page${isDark ? " mi-page--dark" : ""}`}>
        <div className="mi-page-inner">
          <header className="mi-toolbar">
            <div className="mi-toolbar__primary">
              <div>
                <h1 className="cm-title">Call Money</h1>
                <p className="mi-toolbar__meta">
                  <strong>RBI overnight Call Money</strong>
                  <span> · weighted average rate by report date</span>
                </p>
              </div>
              <div className="mi-view-tabs" role="tablist" aria-label="Date range">
                {RANGE_OPTIONS.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    role="tab"
                    aria-selected={rangeKey === option.key}
                    className={`mi-view-tab${
                      rangeKey === option.key ? " mi-view-tab--active" : ""
                    }`}
                    onClick={() => setRangeKey(option.key)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            {latest && (
              <div className="cm-latest">
                <span className="cm-latest__label">Latest</span>
                <span className="cm-latest__value">{latest.weighted.toFixed(2)}%</span>
                <span className="cm-latest__date">{latest.reportDate}</span>
              </div>
            )}
          </header>

          <section className="mi-panel cm-panel" aria-label="Call Money weighted rate chart">
            {loading ? (
              <div className="cm-state">
                <Spin size="large" />
              </div>
            ) : error ? (
              <div className="cm-state cm-state--error">{error}</div>
            ) : filteredPoints.length === 0 ? (
              <div className="cm-state">No Call Money data available</div>
            ) : (
              <div className="cm-chart-wrap">
                <Line data={chartData} options={chartOptions} />
              </div>
            )}
          </section>
        </div>
      </div>
    </LandingLayout>
  );
};

export default CallMoneyChartPage;
